/**
 * **A11 · The role-holder badge** (Stream A, Paper.dc.html): one 3×4 badge a person holding a role
 * this year (Festival Admin → Roles) — the first name large, the full name with the role, the
 * radio channels, and what the role may do, in the drawing's words. The band is the role's: red
 * for a director, navy for a chair (black-and-white safe: the word is printed). The volunteer's
 * badge is the booth's shift tag (drawn on Booth). The drawing's QR (check-out and credentials) is
 * left off until a station reads one: a code nothing reads is not printed.
 */
import { NO_DATA, type PaperPage } from './paper_page.js';

export interface BadgeHolder {
  name: string;
  /** Each role this year: its id (`director`, `area_chair`, …), its name, its area. */
  roles: { role: string; roleName: string; area: string | null }[];
}

/** The radio plan's chairs and security channels; null without one. */
export type BadgeChannels = { chairs: string | null; security: string | null } | null;

const director = (r: { role: string }): boolean => r.role === 'director';

export function roleBadges(year: number, people: BadgeHolder[], ch: BadgeChannels): PaperPage[] {
  const chairs = ch?.chairs ?? NO_DATA;
  return people.map((p) => {
    // The badge is the person's highest role: a director's covers what a chair may.
    const top =
      p.roles.find(director) ?? p.roles.find((r) => r.role === 'area_chair') ?? p.roles[0];
    const first = p.name.split(/\s+/)[0] ?? p.name;
    const isDirector = !!top && director(top);
    const isChair = top?.role === 'area_chair';
    const kind = isDirector ? 'director' : isChair ? 'chair' : (top?.roleName.toLowerCase() ?? '');
    const band = isDirector ? ' · red band' : isChair ? ' · navy band' : '';
    const bigSub = isDirector
      ? `${top.roleName} · Channel ${chairs} and ${ch?.security ?? NO_DATA}`
      : isChair
        ? `${p.name} · ${top.area ?? NO_DATA} chair · Channel ${chairs}`
        : `${p.name} · ${top?.roleName ?? ''}`;
    const may = isDirector
      ? 'All a chair may · approve · Decide · any door'
      : isChair
        ? `Open the ${top.area ?? NO_DATA} · radios · Ask · I spent · PO`
        : null;
    return {
      size: 'badge',
      title: `Apple Fest ${String(year)}`,
      sub: `${kind.toUpperCase()} · all weekend${band}`,
      big: first,
      bigSub,
      blocks: may ? [{ head: 'MAY', cols: '1fr', rows: [[may]] }] : [],
      foot: `Role holder · ${kind}`,
    };
  });
}
